// Separate from `common` so tool labels can later be reconciled with the
// backend-provided ones without touching general UI strings.
export const tools = {
  // Agent display titles from the widget/agent registry. Deliberately distinct from the
  // canonical admin labels below: the registry title is the in-product name (e.g. "Strategy
  // Agent") while the admin list shows the shorter product label (e.g. "Strategy").
  agentsConfig: {
    audienceFlowAgent: 'Audience Flow Agent',
    copyGeneration: 'Copy Generation Agent',
    emailFlowAgent: 'Email Flow Agent',
    externalToolsAgent: 'External Tools',
    ideation: 'Ideation Agent',
    imageGeneration: 'Image Generation Agent',
    loremIpsumAgent: 'Lorem Ipsum Agent',
    socialMediaBrief: 'Social Media Brief Agent',
    strategyAgentV3: 'Strategy Agent',
    templates: 'Templates',
    translationAgent: 'Translation Agent',
    videoGeneration: 'Video Generation Agent',
  },
  audienceFlowAgent: {
    description: 'Understand and engage key audiences',
    name: 'Audience',
  },
  brandMediaLibrary: {
    permissions: {
      noPermission: "You don't have permission to do this in Brand Assets.",
    },
  },
  brandMedia: {
    adUsage: {
      background: 'BG',
      product: 'Product',
      subjectCutout: 'Cutout',
      logo: 'Logo',
      graphicOverlay: 'Graphic',
      other: 'Other',
    },
    title: 'Brand Assets',
    knowledge: 'Knowledge',
    viewToggle: {
      cardsView: 'Card view',
      listView: 'List view',
    },
    type: {
      image: 'Image',
      video: 'Video',
    },
    columns: {
      select: 'Select',
      name: 'Name',
      type: 'Type',
      usage: 'Usage',
      added: 'Added',
    },
    clearBoundTile: 'Remove {{title}} from this layer',
    selectAsset: 'Select {{title}}',
    videoTile: '{{title}} (video)',
    videoPlayer: 'Video player for {{title}}',
    allMedia: 'All assets',
    folders: 'Folders',
    newFolder: 'New folder',
    newSubfolder: 'New subfolder',
    folderName: 'Folder name',
    expandFolder: 'Expand {{name}}',
    collapseFolder: 'Collapse {{name}}',
    rowCount: '({{count}})',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    kindFilter: 'What the library shows',
    kindFilterPrefix: 'Show:',
    kindAll: 'All',
    kindMedia: 'Media',
    kindFonts: 'Fonts',
    searchAssets: 'Search assets',
    mediaSection: 'Media ({{count}})',
    selectedCount_one: '{{count}} selected',
    selectedCount_other: '{{count}} selected',
    selectAllShown: 'Select all shown',
    selectAllMedia: 'Select all media',
    deselectAllMedia: 'Deselect all media',
    moveTo: 'Move to folder',
    moveAsset: 'Move asset',
    noFolder: 'No folder',
    clearSelection: 'Clear selection',
    delete: 'Delete',
    deleteAsset: 'Delete asset',
    deleteSelectedTitle_one: 'Delete {{count}} asset?',
    deleteSelectedTitle_other: 'Delete {{count}} assets?',
    deleteWarning_one:
      'It stops appearing in this library and in every picker. Boards that already place it keep what they show.',
    deleteWarning_other:
      'They stop appearing in this library and in every picker. Boards that already place them keep what they show.',
    deleted_one: '{{count}} asset deleted.',
    deleted_other: '{{count}} assets deleted.',
    deleteFailed: 'The assets could not be deleted.',
    // A selection can span assets and fonts, and neither noun is honest about the other, so the
    // mixed case counts items instead of picking a side.
    deleteSelectedItemsTitle_one: 'Delete {{count}} item?',
    deleteSelectedItemsTitle_other: 'Delete {{count}} items?',
    deletedItems_one: '{{count}} item deleted.',
    deletedItems_other: '{{count}} items deleted.',
    thisAsset: 'this asset',
    loading: 'Loading the library',
    failed: 'The library could not be loaded.',
    failedDescription: 'Something went wrong on the way. Nothing has been lost.',
    retry: 'Try again',
    emptyTitle: 'Nothing in this library yet',
    emptyDescription:
      'Upload images, video and typefaces here and they become available to every brief and layout in the workspace.',
    noMedia: 'No images or video yet',
    noMatchesTitle: 'Nothing matches "{{search}}"',
    noFilterMatchesTitle: 'Nothing matches the current filters',
    narrowedDescription: 'Try a different phrase, or clear the filters to see everything.',
    folderActions: 'Actions for {{name}}',
    folderMenu: 'Folder actions',
    folderTrail: 'Folders above this one',
    hideRail: 'Hide panel',
    showRail: 'Show panel',
    rename: 'Rename',
    deleteFolder: 'Delete folder',
    // Items rather than assets: a folder holds fonts as well, and the count beside it in the rail
    // has always been both.
    deleteFolderWarning_one:
      'The folders beneath it go too, and the {{count}} item filed in it and in its subfolders is deleted with it. Boards that already place those items keep what they show.',
    deleteFolderWarning_other:
      'The folders beneath it go too, and the {{count}} items filed in it and in its subfolders are deleted with it. Boards that already place those items keep what they show.',
    deleteEmptyFolderWarning:
      'The folders beneath it go too. Nothing is filed in it, so nothing is deleted.',
    folderDeleted_one: '{{name}} deleted, with {{count}} item.',
    folderDeleted_other: '{{name}} deleted, with {{count}} items.',
    folderDeletedEmpty: '{{name}} deleted.',
    folderDeleteFailed: 'The folder could not be deleted.',
    detailPanel: 'Asset details',
    railTitle: 'Browse',
    tags: 'Tags',
    searchRail: 'Search folders and tags',
    closeSearch: 'Close search',
    searchTags: 'Search tags',
    noFoldersMatch: 'No folders match "{{search}}".',
    noTagsMatch: 'No tags match "{{search}}".',
    tagSelected: 'Tag selected',
    tagSelectedNeedsSelection: 'Choose assets first.',
    tagsCatchingUp: 'Catching up with a tag change. This is available again in a moment.',
    tagSelectedTitle_one: 'Tag {{count}} asset',
    tagSelectedTitle_other: 'Tag {{count}} assets',
    tagSelectedKey: 'Key',
    tagSelectedValue: 'Value',
    tagSelectedApply: 'Add tag',
    tagSelectedDone_one: 'Tag added to {{count}} asset.',
    tagSelectedDone_other: 'Tag added to {{count}} assets.',
    tagSelectedFailed: 'The tag could not be added to every asset.',
    tagSelectedPartial_one:
      'Tag added to {{done}}. {{count}} asset would not take it, and is still selected: add the tag again to retry it.',
    tagSelectedPartial_other:
      'Tag added to {{done}}. {{count}} assets would not take it, and are still selected: add the tag again to retry them.',
    applyTag_one: 'Add "{{value}}" to {{count}} selected asset',
    applyTag_other: 'Add "{{value}}" to {{count}} selected assets',
    tagAlreadyThere_one: 'The selected asset already carries this tag.',
    tagAlreadyThere_other: 'All {{count}} selected assets already carry this tag.',
    untagSelected_one: 'Remove "{{value}}" from {{count}} selected asset',
    untagSelected_other: 'Remove "{{value}}" from {{count}} selected assets',
    untagSelectedDone_one: 'Tag removed from {{count}} asset.',
    untagSelectedDone_other: 'Tag removed from {{count}} assets.',
    untagSelectedFailed: 'The tag could not be removed from every asset.',
    untagSelectedPartial_one:
      'Tag removed from {{done}}. {{count}} asset kept it, and is still selected: remove the tag again to retry it.',
    untagSelectedPartial_other:
      'Tag removed from {{done}}. {{count}} assets kept it, and are still selected: remove the tag again to retry them.',
    tagNotThere_one: 'The selected asset does not carry this tag.',
    tagNotThere_other: 'None of the {{count}} selected assets carry this tag.',
    tagActions: 'Actions for {{key}}: {{value}}',
    renameTagValue: 'Rename the tag "{{value}}"',
    deleteTagEverywhere: 'Delete everywhere',
    modelTagKeeps:
      'The "{{key}}" tag is written by image analysis and keeps a value. Analysis refills it on the next pass, so removing it would not stick. Change the value, or rename it across the library.',
    modelTagAwaitingValue: 'Type the replacement',
    modelTagPendingValue: 'Needs a value before you finish. Leaving now keeps the old one.',
    modelTagNeedsValue_one:
      'The "{{key}}" tag still needs a value. Type its replacement, or reopen the asset to put the old one back.',
    modelTagNeedsValue_other:
      '{{count}} tags still need a value, starting with "{{key}}". Type their replacements, or reopen the asset to put the old ones back.',
    tagRenamed_one: 'Renamed to "{{to}}" on {{count}} asset.',
    tagRenamed_other: 'Renamed to "{{to}}" on {{count}} assets.',
    tagRenameFailed: 'The tag could not be renamed.',
    mergeTagTitle: 'Merge into "{{to}}"?',
    mergeTagWarning:
      '"{{to}}" is already in use, so renaming "{{from}}" to it folds the two into one tag. This cannot be undone. Image layers already set up with "{{from}}" keep that wording.',
    mergeTagConfirm: 'Merge tags',
    mergeTagPending: 'Merging...',
    tagMerged_one: 'Merged "{{from}}" into "{{to}}" on {{count}} asset.',
    tagMerged_other: 'Merged "{{from}}" into "{{to}}" on {{count}} assets.',
    deleteTagWarning:
      'This takes "{{value}}" off every asset in the library that carries it, and cannot be undone. Image layers already set up with it keep that wording.',
    tagDeleted_one: 'Removed "{{value}}" from {{count}} asset.',
    tagDeleted_other: 'Removed "{{value}}" from {{count}} assets.',
    tagDeleteFailed: 'The tag could not be removed.',
    facetInert: 'Every item here carries this, so it cannot narrow anything.',
    tagFilter: '{{key}}: {{value}}',
    clearTagFilter: 'Stop filtering by {{key}}: {{value}}',
    autoTags: 'Auto',
    autoTagLabel: '{{key}} {{value}}, set by ingest',
    noTagsYet: 'No tags on this yet.',
    tagging: 'Tagging this asset. Its tags appear here when it finishes.',
    ingestingTile: 'Being tagged and described',
    tileImageFailed: "Couldn't load this image",
    taggingUnfinished: 'This asset has not been tagged.',
    retryTagging: 'Autotag',
    retryTaggingHint:
      'Runs over every untagged asset, not just this one. Tags already set are left alone, so image layers that select on them keep the pictures they have.',
    taggingRetried_one: 'Tagging started again for {{count}} asset.',
    taggingRetried_other: 'Tagging started again for {{count}} assets.',
    taggingRetryFailed: 'Tagging could not be started again.',
    describing: 'Describing this asset. Its description appears here when it finishes.',
    notDescribed: 'This asset has not been described.',
    ingestOwned: 'Set by ingest. A re-run replaces it.',
    addTag: 'Add tag',
    editTags: 'Edit',
    doneEditingTags: 'Done',
    addTagKeyPlaceholder: 'e.g. product, campaign',
    addValuePlaceholder: 'Type a value and press Enter',
    removeTagValue: 'Remove {{value}}',
    removeTagGroup: 'Remove {{key}}',
    reservedTagKey: 'Set by ingest. Choose a different name.',
    duplicateTagKey: 'Already exists. Add the value under {{key}} instead.',
    tagsUpdated: 'Tags updated.',
    tagsUpdateFailed: 'The tags could not be updated.',
    close: 'Close',
    enlarge: 'Open full size',
    notRecorded: 'Not recorded',
    resizePanel: 'Resize panel',
    uploadSheet: {
      title: 'Upload files',
      filesLabel: 'Choose files to add to the brand library',
      dropFilesHere: 'Drop files here or click to browse',
      acceptedFormats: 'PNG, JPEG, WEBP, SVG, MP4, TTF, OTF, WOFF, WOFF2 - Max 100MB',
      cancel: 'Cancel',
      upload: 'Upload',
      uploadWithCount_one: 'Upload {{count}} file',
      uploadWithCount_other: 'Upload {{count}} files',
    },
    sections: {
      tags: 'Tags',
      descriptions: 'Descriptions',
      objects: 'In focus (bounding boxes)',
      properties: 'Properties',
      licence: 'Licence',
    },
    objects: {
      none: 'Nothing in focus',
      toggle: 'Show bounding boxes',
      box: 'x {{x}}, y {{y}} · w {{w}}, h {{h}}',
    },
    properties: {
      literalDescription: 'Description',
      styleDescription: 'Style',
      dimensions: 'Dimensions',
      orientation: 'Orientation',
      mediaType: 'Media type',
      placementClass: 'Placement',
      scene: 'Scene',
      shot: 'Shot',
      markets: 'Markets',
      everyMarket: 'Every market',
      added: 'Added',
      licenseTiers: 'Licence tiers',
      licenseExpiry: 'Licence expiry',
    },
  },
  brandFont: {
    specimenSample: 'Aa',
    sectionTitle: 'Fonts ({{count}})',
    selectAllFonts: 'Select all fonts',
    deselectAllFonts: 'Deselect all fonts',
    loading: 'Loading the fonts',
    failed: 'The fonts could not be loaded.',
    failedDescription: 'Something went wrong on the way. Nothing has been lost.',
    empty: 'No fonts uploaded yet.',
    thisFont: 'this font',
    deleteWarning_one:
      'It stops appearing in this library and in every picker. Pieces that already set type in it keep what they show.',
    deleteWarning_other:
      'They stop appearing in this library and in every picker. Pieces that already set type in them keep what they show.',
    deleteSelectedTitle_one: 'Delete {{count}} font?',
    deleteSelectedTitle_other: 'Delete {{count}} fonts?',
    deletedCount_one: '{{count}} font deleted.',
    deletedCount_other: '{{count}} fonts deleted.',
    columns: {
      select: 'Select',
      family: 'Family',
      weight: 'Weight',
      style: 'Style',
      added: 'Added',
    },
    selectFont: 'Select {{name}}',
    detailPanel: 'Font details',
    close: 'Close',
    resizePanel: 'Resize panel',
    notRecorded: 'Not recorded',
    specimenSentence: 'Sphinx of black quartz, judge my vow',
    moveFont: 'Move to folder',
    deleteFontAction: 'Delete font',
    replaceFontAction: 'Replace file',
    replacingFont: 'Replacing…',
    replaced: 'Font file replaced',
    replaceFailed: 'The font file could not be replaced.',
    sections: {
      properties: 'Properties',
      licence: 'Licence',
    },
    properties: {
      family: 'Family',
      weight: 'Weight',
      style: 'Style',
      format: 'Format',
      fileSize: 'File size',
      filename: 'File',
      added: 'Added',
      foundry: 'Foundry',
      designer: 'Designer',
      version: 'Version',
      copyright: 'Copyright',
      licenceTerms: 'Licence',
      licenceUrl: 'Licence URL',
    },
  },
  copyGenerationAgent: {
    description: 'Write channel-ready brand copy',
    name: 'Copy',
  },
  contentStudioV2: {
    canvas: {
      ungrouped: 'Ungrouped',
    },
    inspector: {
      auto: 'Auto',
      backToBrandDefault: 'Back to the brand default',
      changeImage: 'Change image',
      chooseImage: 'Choose an image',
      clearImage: 'Remove image',
      close: 'Close inspector',
      copy: 'Copy',
      design: 'Design',
      doesNotFit: 'This copy does not fit its box',
      fit: {
        fitsAll_one: 'Fits the one format on this board',
        fitsAll_other: 'Fits all {{count}} formats',
        fitsSampled_one: 'Fits the format checked',
        fitsSampled_other: 'Fits the {{count}} formats checked',
        wontShip: "{{failing}} of {{total}} won't ship",
        tooLongFor: 'Will not ship on {{failing}} of {{total}} formats',
      },
      layout: 'Layout',
      boundImage: 'Currently bound',
      galleryEmpty: 'No media matches this frame.',
      searchEmpty: 'Nothing in the library matches "{{search}}".',
      galleryFailed: 'The brand assets could not be loaded.',
      galleryFailedDescription: 'Something went wrong on the way. Nothing has been lost.',
      galleryRetry: 'Try again',
      galleryLoading: 'Loading media',
      galleryTitle: 'Brand Assets',
      libraryLocation: 'Library location',
      libraryTags: 'Tags',
      openLibrary: 'Open Library',
      searchMedia: 'Search media',
      upload: 'Upload',
      uploadMedia: 'Upload media',
      imageEmpty: 'Nothing bound',
      imageMixed: 'Several images',
      imageOption: 'Option {{number}}',
      media: 'Media',
      mixed: 'Multiple values',
      nothingDeclared: 'This frame declares nothing editable',
      partial: '{{applied}} of {{selected}}',
      range: '{{min}} to {{max}}',
      selectAFrame: 'Select a frame to edit it',
      everyFormat_one: 'Every format ({{count}})',
      everyFormat_other: 'Every format ({{count}})',
      selected_one: '{{count}} frame selected',
      selected_other: '{{count}} frames selected',
    },
  },
  creativeFlowsApp: {
    description: 'Create custom Creative Workflows',
    name: 'Creative Flows',
  },
  doughboyImageAgent: {
    description: 'Generate brand-safe Doughboy images.',
    name: 'Image - Doughboy',
  },
  emailFlowAgent: {
    description: 'Produce on-brand email templates',
    name: 'Email',
  },
  externalToolsAgent: {
    description: 'Discover external tools',
    name: 'External Tools',
  },
  flowDigestAgent: {
    description: 'Digest flow analysis and reporting',
    name: 'Flow Digest',
  },
  ideationAgent: {
    description: 'Explore and shape creative concepts',
    name: 'Ideation',
  },
  ideationBriefIntakeAgent: {
    description: 'Create clear, aligned project briefs',
    name: 'Briefing Intake',
  },
  imageGenerationAgent: {
    description: 'Produce on-brand image assets',
    name: 'Image',
  },
  managedTools: {
    contentStudio: {
      description: 'Design and produce on-brand creative at scale.',
      name: 'Content Studio',
    },
    contentTest: {
      description: 'Evaluate concepts against personas and track results over time.',
      name: 'Content Test',
    },
    fabric: {
      description:
        "Turn a Brand's evidence and performance data into governed knowledge and auditable analysis.",
      name: 'Fabric',
    },
    focusGroups: {
      description: 'Simulate audience reactions and gather feedback from AI personas.',
      name: 'Focus Groups',
    },
    iq: {
      description: 'Turn audience research into insights and analysis you can act on.',
      name: 'IQ',
    },
    nacbIntake: {
      description: 'Trigger an unplanned request to Agency partners via a structured intake form.',
      name: 'NACB Trigger Intake',
    },
    reelsCreator: {
      adminDescription:
        'Controls access to the in-app Reels Creator, which turns a brief into short-form video reels.',
      description: 'Turn a brief into short-form video reels.',
      name: 'Reels Creator',
    },
  },
  sentinelBuddyAgent: {
    description: 'Automate brand and quality checks',
    name: 'Quality Assurance',
  },
  strategyAgentV3: {
    description: 'Build data-driven strategic paths',
    name: 'Strategy',
  },
  templatesAgent: {
    description: 'Produce on-brand email templates',
    name: 'Email 2.0',
  },
  toolsHub: {
    about: {
      description:
        "Find the right app or agent for whatever you're trying to get done \u2014 from first idea to final delivery.",
      putAgentsToWork: "Put specialized agents to work on the tasks you'd rather not do by hand.",
      reachForTheStageYouAreIn: "Reach for the tool that fits the stage of work you're in.",
      title: 'About tools',
      withoutLeavingTheConsole:
        'Plan, create, scale, and deliver work without leaving the console.',
    },
    browseByCategory: {
      browseOurCuratedList: 'Browse our curated list of tools per category',
      notSureWhereToStart: 'Not sure where to start?',
    },
    createPage: {
      allTools: 'All tools',
      back: 'Back',
      smartStarts: 'Smart starts',
    },
    deliverPage: {
      allTools: 'All tools',
      back: 'Back',
    },
    featuredCard: {
      featured: 'Featured',
    },
    guidedLearningSection: {
      learnTheToolsToBuild: 'Learn the Tools to Build Your Best Work.',
      videosDroppingSoon: 'Videos dropping soon.',
    },
    guidedLearningWorkflow: {
      guidedLearningExpandingYourReach: 'Guided Learning: Expanding Your Reach',
      guidedLearningTheOpportunitiesWorkflow: 'Guided Learning: The Opportunities Workflow',
      seeMore: 'See more',
    },
    planPage: {
      allTools: 'All tools',
      back: 'Back',
      smartStarts: 'Smart starts',
    },
    scalePage: {
      allTools: 'All tools',
      back: 'Back',
      smartStarts: 'Smart starts',
    },
    toolCatalog: {
      agent: 'Agent',
      agents: 'Agents',
      allCategories: 'All categories',
      allTypes: 'All types',
      app: 'App',
      apps: 'Apps',
      category: 'Category',
      configure: 'Configure',
      noToolsAvailable: 'No tools available. Please contact your administrator to get access.',
      noToolsMatchYourFilters: 'No tools match your filters',
      openApp: 'Open app',
      searchToolsByName: 'Search tools by name...',
      searchToolsByNameLabel: 'Search tools by name',
      startChat: 'Start chat',
      type: 'Type',
    },
    tools: 'Tools',
    toolsCategoryPage: {
      back: 'Back',
      smartStarts: 'Smart Starts',
      toolsTo: 'Tools to {{tabLabel}}',
    },
    toolsHubConstants: {
      adaptation: 'Adaptation',
      automateBrandAndQuality: 'Automate brand and quality checks',
      brandVsAudience: 'Brand vs. Audience',
      buildSmartCraftFaster: 'Build smart. Craft faster.',
      contentStudio: 'Content Studio',
      create: 'Create',
      curateYourStrategy: 'Curate Your Strategy',
      deliver: 'Deliver',
      findWhatSTrending: "Find What's Trending",
      fromTrendToBriefIn: 'From Trend to Brief in Seconds',
      importDesignSystemsAndGenerateAds: 'Import design systems and generate ads',
      introToPlan: 'Intro to Plan',
      launchCleanMoveFaster: 'Launch clean. Move faster.',
      learnFastGrowFaster: 'Learn fast. Grow faster.',
      opportunitiesRoom: 'Opportunities Room',
      pinningTopTrends: 'Pinning Top Trends',
      plan: 'Plan',
      planningYourFuture: 'Planning Your Future',
      qualityAssurance: 'Quality assurance',
      scale: 'Scale',
      scaleVariationsAcrossMarkets: 'Scale variations across markets',
      seeAheadShapeStrategy: 'See ahead. Shape strategy.',
      smartFilteringTips: 'Smart Filtering Tips',
      smartPlanningTips: 'Smart Planning Tips',
      spotTimelyBrandSafeOpportunities: 'Spot timely, brand-safe opportunities',
      stepByStep: 'Step-by-Step',
      strategyStartsHere: 'Strategy Starts Here',
      support: 'Support',
      theFinalStep: 'The Final Step',
      theOpportunitiesAgent: 'The Opportunities Agent',
    },
  },
  translationAgent: {
    description: 'Translate copy with brand accuracy',
    name: 'Translation',
  },
  videoGenerationAgent: {
    description: 'Create high-quality video content',
    name: 'Video',
  },
} as const;
